import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../App";
import { toast } from "sonner";
import { ArrowLeft, Send, X, ChevronDown } from "lucide-react";
import { IconAssistant, IconShield, IconWarning, IconContract, IconChat } from "../components/MinimalIcons";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Categorias de ajuda do assistente
const HELP_CATEGORIES = [
  {
    id: "platform",
    title: "Ajuda da Plataforma",
    icon: IconAssistant,
    questions: [
      "Como funciona o pagamento protegido?",
      "O que é escrow?",
      "Como assinar um contrato?",
      "Como aprovar etapas?",
      "O que acontece se houver problema?"
    ]
  },
  {
    id: "service",
    title: "Sobre meu Serviço",
    icon: IconContract,
    questions: [
      "Analise meu orçamento atual",
      "Quais evidências devo anexar?",
      "Recomende etapas para meu serviço",
      "Qual prazo realista para este serviço?",
      "Quais riscos devo considerar?"
    ]
  },
  {
    id: "security",
    title: "Segurança e Alertas",
    icon: IconShield,
    questions: [
      "Como identificar golpes?",
      "Meu orçamento está seguro?",
      "Quais sinais de risco devo observar?",
      "Posso pagar fora do app?",
      "O que fazer se o prestador pedir 100% adiantado?"
    ]
  },
  {
    id: "legal",
    title: "Entender meu Contrato",
    icon: IconContract,
    questions: [
      "O que significa retenção?",
      "Quando o dinheiro será liberado?",
      "O que acontece se atrasar?",
      "O que acontece se ficar mal feito?",
      "O que acontece se eu cancelar?"
    ]
  }
];

// Quick actions para o chat
const QUICK_ACTIONS = [
  { label: "Solicitar detalhamento", action: "detail" },
  { label: "Solicitar checklist", action: "checklist" },
  { label: "Solicitar etapas", action: "milestones" },
  { label: "Solicitar garantia", action: "guarantee" },
  { label: "Gerar contraproposta", action: "counter" }
];

export default function AssistantPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(`assistant_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [contextData, setContextData] = useState(null);
  const messagesEndRef = useRef(null);

  // Contexto passado pela navegação (ex: de um orçamento ou contrato)
  useEffect(() => {
    if (location.state?.context) {
      setContextData(location.state.context);
    }
  }, [location.state]);

  useEffect(() => {
    // Mensagem inicial do assistente
    setMessages([{
      role: "assistant",
      content: `Olá${user?.name ? `, ${user.name.split(" ")[0]}` : ""}! Sou o Assistente eZelo. Posso ajudar você com:

• **Plataforma** — Como funciona o pagamento protegido, contratos, etc.
• **Seu Serviço** — Análise de orçamentos, recomendações, prazos
• **Segurança** — Detecção de riscos, alertas anti-golpe
• **Jurídico** — Entender seu contrato, direitos e obrigações

Como posso ajudar você hoje?`,
      timestamp: new Date()
    }]);
  }, [user?.name]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (messageText = inputMessage) => {
    if (!messageText.trim() || loading) return;

    const userMessage = {
      role: "user",
      content: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/assistant/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({
          session_id: sessionId,
          message: messageText,
          context: contextData,
          category: selectedCategory
        })
      });

      if (!response.ok) throw new Error("Erro na comunicação");

      const data = await response.json();
      
      setMessages(prev => [...prev, {
        role: "assistant",
        content: data.response,
        timestamp: new Date(),
        alerts: data.alerts,
        suggestions: data.suggestions
      }]);

    } catch (error) {
      toast.error("Erro ao processar mensagem");
      console.error("Assistant error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = (action) => {
    const actionMessages = {
      detail: "Preciso de mais detalhes sobre o escopo do serviço",
      checklist: "Gere um checklist de entrega para este serviço",
      milestones: "Sugira etapas (milestones) para dividir este serviço",
      guarantee: "Qual retenção/garantia é recomendada?",
      counter: "Ajude-me a gerar uma contraproposta"
    };
    sendMessage(actionMessages[action] || action);
    setShowQuickActions(false);
  };

  const handleCategoryQuestion = (question) => {
    sendMessage(question);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="assistant-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-neutral-100">
        <div className="px-5 h-14 flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-neutral-50 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-neutral-700" strokeWidth={1.5} />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-neutral-900 flex items-center justify-center">
              <IconAssistant className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-medium text-neutral-900 text-sm">Assistente eZelo.</p>
              <p className="text-neutral-400 text-xs">IA • Plataforma • Segurança • Jurídico</p>
            </div>
          </div>
        </div>
      </header>

      {/* Chat Messages */}
      <main className="flex-1 overflow-y-auto px-5 py-6">
        <div className="max-w-2xl mx-auto space-y-4">
          {messages.map((message, index) => (
            <div key={index} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] ${message.role === "user" ? "order-1" : ""}`}>
                <div className={`rounded-2xl px-4 py-3 ${
                  message.role === "user" 
                    ? "bg-neutral-900 text-white rounded-br-md" 
                    : "bg-neutral-100 text-neutral-900 rounded-bl-md"
                }`}>
                  <div className="text-sm whitespace-pre-wrap prose prose-sm max-w-none">
                    {message.content.split('\n').map((line, i) => {
                      // Parse bold text
                      const parts = line.split(/\*\*(.*?)\*\*/g);
                      return (
                        <p key={i} className={i > 0 ? "mt-2" : ""}>
                          {parts.map((part, j) => 
                            j % 2 === 1 ? <strong key={j}>{part}</strong> : part
                          )}
                        </p>
                      );
                    })}
                  </div>
                </div>

                {/* Alerts */}
                {message.alerts && message.alerts.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {message.alerts.map((alert, i) => (
                      <div key={i} className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-xl">
                        <IconWarning className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-800">{alert}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Suggestions */}
                {message.suggestions && message.suggestions.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {message.suggestions.map((suggestion, i) => (
                      <button
                        key={i}
                        onClick={() => sendMessage(suggestion)}
                        className="px-3 py-1.5 bg-white border border-neutral-200 rounded-full text-xs text-neutral-700 hover:border-neutral-300 transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-neutral-100 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-neutral-400 rounded-full animate-pulse"></div>
                  <div className="w-2 h-2 bg-neutral-400 rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-neutral-400 rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Help Categories - Show when no messages sent yet */}
        {messages.length <= 1 && (
          <div className="max-w-2xl mx-auto mt-6 space-y-4">
            {HELP_CATEGORIES.map((category) => (
              <div key={category.id} className="border border-neutral-100 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setSelectedCategory(selectedCategory === category.id ? null : category.id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-neutral-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-neutral-100 flex items-center justify-center">
                      <category.icon className="w-5 h-5 text-neutral-700" />
                    </div>
                    <span className="font-medium text-neutral-900 text-sm">{category.title}</span>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-neutral-400 transition-transform ${selectedCategory === category.id ? 'rotate-180' : ''}`} strokeWidth={1.5} />
                </button>
                
                {selectedCategory === category.id && (
                  <div className="px-4 pb-4 space-y-2">
                    {category.questions.map((question, i) => (
                      <button
                        key={i}
                        onClick={() => handleCategoryQuestion(question)}
                        className="w-full text-left px-4 py-3 bg-neutral-50 rounded-xl text-sm text-neutral-700 hover:bg-neutral-100 transition-colors"
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Quick Actions Panel */}
      {showQuickActions && (
        <div className="absolute bottom-20 left-0 right-0 bg-white border-t border-neutral-100 p-4 animate-in slide-in-from-bottom">
          <div className="flex items-center justify-between mb-3">
            <span className="font-medium text-neutral-900 text-sm">Ações Rápidas</span>
            <button onClick={() => setShowQuickActions(false)}>
              <X className="w-5 h-5 text-neutral-400" strokeWidth={1.5} />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {QUICK_ACTIONS.map((action, i) => (
              <button
                key={i}
                onClick={() => handleQuickAction(action.action)}
                className="px-4 py-2 bg-neutral-100 rounded-full text-sm text-neutral-700 hover:bg-neutral-200 transition-colors"
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="sticky bottom-0 bg-white border-t border-neutral-100 px-5 py-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-end gap-3">
            <button
              onClick={() => setShowQuickActions(!showQuickActions)}
              className="w-12 h-12 rounded-xl bg-neutral-100 flex items-center justify-center flex-shrink-0 hover:bg-neutral-200 transition-colors"
              data-testid="quick-actions-btn"
            >
              <IconChat className="w-5 h-5 text-neutral-700" />
            </button>
            <div className="flex-1 relative">
              <textarea
                placeholder="Digite sua pergunta..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full min-h-[48px] max-h-32 px-4 py-3 bg-neutral-100 border-0 rounded-xl resize-none text-sm focus:outline-none focus:ring-2 focus:ring-neutral-200"
                rows={1}
                disabled={loading}
                data-testid="chat-input"
              />
            </div>
            <button 
              onClick={() => sendMessage()}
              disabled={!inputMessage.trim() || loading}
              className="w-12 h-12 rounded-xl bg-neutral-900 flex items-center justify-center flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-800 transition-colors"
              data-testid="send-btn"
            >
              <Send className="w-5 h-5 text-white" strokeWidth={1.5} />
            </button>
          </div>
          <p className="text-[10px] text-neutral-400 mt-2 text-center">
            Assistente eZelo. • IA GPT-5.2 • Suas informações são protegidas
          </p>
        </div>
      </div>
    </div>
  );
}
